import React from "react";
import { Plus, MessageSquare, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useUiMode } from "../hooks/useUiMode";

export default function ConversationList({
  activeId, onSelect, onNew,
  tenants, tenantSchema, onTenantChange, tenantsLoading, tenantsError,
  provider, onProviderChange,
  mode, onModeChange,
  collapsed, onToggleCollapsed,
}) {
  const { uiMode, setUiMode } = useUiMode();

  const toggleButton = (
    <button
      type="button"
      onClick={onToggleCollapsed}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
    >
      {collapsed ? (
        <ChevronRight className="w-4 h-4" strokeWidth={2} />
      ) : (
        <ChevronLeft className="w-4 h-4" strokeWidth={2} />
      )}
    </button>
  );

  if (collapsed) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center p-2 border-b border-gray-200">
          {toggleButton}
        </div>
      </div>
    );
  }
  // Model dropdown is only meaningful when the AI2 engine is selected.
  // AI uses the legacy /ai namespace (no model selector); AI2 Local hardcodes
  // provider:"local" on the backend regardless of this value.
  const modelActive = mode === "ai2";

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-end px-2 pt-2">
        {toggleButton}
      </div>
      <div className="p-4 border-b border-gray-200 space-y-3">
        {/* Engine picker — selects which socket namespace / server path to use */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">
            Engine
          </label>
          <div className="relative">
            <select
              value={mode || "ai2"}
              onChange={(e) => onModeChange(e.target.value)}
              className="w-full appearance-none px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors"
            >
              <option value="ai-assistant">AI Assistant (recommended)</option>
              <option value="ai-assistant-llm">AI Assistant (LLM-driven)</option>
              <option value="ai">AI (legacy v1)</option>
              <option value="ai2">AI2 (legacy v2)</option>
              <option value="ai2-local">AI2 Local (legacy v2 local)</option>
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
          </div>
        </div>

        {/* Provider picker — active only when Engine is AI2 */}
        <div className={modelActive ? "" : "opacity-40 pointer-events-none select-none"}>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">
            Model
          </label>
          <div className="relative">
            <select
              value={provider || "openai"}
              onChange={(e) => onProviderChange(e.target.value)}
              className="w-full appearance-none px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors"
            >
              <option value="openai">OpenAI (gpt-5-mini)</option>
              <option value="anthropic">Anthropic (Claude)</option>
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
          </div>
        </div>

        {/* Tenant picker */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">
            Tenant
          </label>
          {tenantsLoading ? (
            <div className="text-xs text-gray-400 italic">Loading…</div>
          ) : tenantsError ? (
            <div className="text-xs text-red-600">Failed to load tenants</div>
          ) : (
            <div className="relative">
              <select
                value={tenantSchema || ""}
                onChange={(e) => onTenantChange(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors"
              >
                {tenants.map((t) => {
                  const value = t.name || t.schema || t;
                  const label = t.subdomain || t.name || t.schema || t;
                  return <option key={value} value={value}>{label}</option>;
                })}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
            </div>
          )}
        </div>

        {/* View mode toggle — Dev shows technical detail (SQL, tokens,
            row counts, parse-error text); Prod hides it. The toggle
            triggers a socket reconnect so the new mode reaches the
            server-side prompt builder for subsequent messages. */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">
            Mode
          </label>
          <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden text-sm">
            {["dev", "prod"].map((m) => {
              const active = uiMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => uiMode !== m && setUiMode(m)}
                  className={
                    "px-3 py-1.5 transition-colors " +
                    (active
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:bg-gray-50")
                  }
                >
                  {m === "dev" ? "Dev" : "Prod"}
                </button>
              );
            })}
          </div>
        </div>

        {/* New conversation button */}
        <button
          onClick={onNew}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          New conversation
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-3">
        <div className="px-2 mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
          Today
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-blue-50 text-blue-900 text-sm cursor-default">
          <MessageSquare className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="truncate">Current conversation</span>
        </div>
        <p className="px-3 mt-6 text-xs text-gray-400 leading-relaxed">
          Conversation history will appear here once persistence lands.
        </p>
      </div>
    </div>
  );
}
